import { useId, useState } from 'react'
import { ArrowUp, AudioLines, ListChecks, Loader2, Mic, MicOff, PhoneOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { VoiceStatus } from '@/src/lib/voice/events'
import type { VoiceLine } from './useVoiceConversation'

/** What the call is doing, in a word or two; the widget's launcher shows it too. */
export const VOICE_STATUS_LABEL: Record<VoiceStatus, string> = {
  idle: 'Voice is off',
  connecting: 'Connecting…',
  reconnecting: 'Reconnecting…',
  listening: 'Listening',
  thinking: 'Thinking…',
  speaking: 'Speaking',
  error: 'Voice stopped',
}

interface Props {
  status: VoiceStatus
  lines: VoiceLine[]
  muted: boolean
  error: string | null
  onToggleMute: () => void
  onEnd: () => void
  className?: string
}

interface PanelProps extends Props {
  /** Changes and replies waiting on the person's approval in the conversation. */
  awaiting?: number
  /** Brings the first of them into view. */
  onShowAwaiting?: () => void
}

/**
 * The live voice conversation, docked under the conversation: what the
 * assistant is doing, the last thing said, and mute and hang up. It stays
 * small so the conversation keeps the room: the cards Beacon puts up for
 * approval are there, and approving one is a tap during the call. The rest of
 * what was said opens on request, in a box of its own height; anything
 * waiting on approval is named here, a tap from view.
 */
export function VoicePanel({
  status,
  lines,
  muted,
  error,
  onToggleMute,
  onEnd,
  className,
  awaiting = 0,
  onShowAwaiting,
}: PanelProps) {
  const live = status !== 'idle' && status !== 'error'
  const [expanded, setExpanded] = useState(false)
  const transcriptId = useId()
  const shown = expanded ? lines : lines.slice(-1)
  return (
    <section
      aria-label="Voice conversation"
      className={cn('min-w-0 space-y-2 border-t border-border bg-muted/30 px-3 py-2', className)}
    >
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium" role="status">
          {status === 'connecting' || status === 'reconnecting' || status === 'thinking' ? (
            <Loader2 aria-hidden className="size-4 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <span
              aria-hidden
              className={cn(
                'size-2.5 shrink-0 rounded-full',
                status === 'speaking' && 'animate-pulse bg-brand',
                status === 'listening' && (muted ? 'bg-muted-foreground' : 'bg-done'),
                status === 'error' && 'bg-blocked',
              )}
            />
          )}
          <span className="truncate">{muted && live ? 'Muted' : VOICE_STATUS_LABEL[status]}</span>
        </div>
        {live && (
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleMute}
            aria-pressed={muted}
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <MicOff /> : <Mic />}
            {muted ? 'Unmute' : 'Mute'}
          </Button>
        )}
        <Button variant={live ? 'destructive' : 'outline'} size="sm" onClick={onEnd}>
          <PhoneOff />
          {live ? 'End' : 'Close'}
        </Button>
      </div>

      {awaiting > 0 && onShowAwaiting && (
        <Button
          variant="outline"
          size="sm"
          onClick={onShowAwaiting}
          className="w-full border-waiting/60 bg-background"
        >
          <ListChecks className="text-waiting" />
          {awaiting === 1 ? '1 action to approve' : `${awaiting} actions to approve`}
          <ArrowUp aria-hidden className="ml-auto" />
        </Button>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {lines.length > 0 && (
        <div className="flex min-w-0 items-start gap-2">
          <ol
            id={transcriptId}
            className={cn(
              'min-w-0 flex-1 space-y-1 text-sm break-words',
              expanded && 'max-h-40 overflow-y-auto',
            )}
            aria-label="What was said"
          >
            {shown.map((line) => (
              <li
                key={line.id}
                className={cn(
                  line.role === 'user' && 'text-muted-foreground',
                  !expanded && 'line-clamp-2',
                )}
              >
                <span className="font-medium">{line.role === 'user' ? 'You' : 'Beacon'}:</span>{' '}
                {line.text}
              </li>
            ))}
          </ol>
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            aria-controls={transcriptId}
            className="shrink-0 rounded-sm px-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            {expanded ? 'Less' : 'Transcript'}
          </button>
        </div>
      )}
      {live && lines.length === 0 && status !== 'connecting' && (
        <p className="text-sm text-muted-foreground">
          Go ahead: ask about an inquiry, a consignment or a client.
        </p>
      )}
    </section>
  )
}

/**
 * Conversation mode on the assistant's home: the same call as the panel, given
 * the middle of the page while nothing has been written yet. Same controls and
 * the same status, so it reads as one call wherever it is shown.
 */
export function VoiceStage({ status, lines, muted, error, onToggleMute, onEnd }: Props) {
  const live = status !== 'idle' && status !== 'error'
  const waiting = status === 'connecting' || status === 'reconnecting' || status === 'thinking'
  return (
    <section
      aria-label="Voice conversation"
      className="flex w-full flex-col items-center gap-6 text-center"
    >
      <div aria-hidden className="relative flex size-36 items-center justify-center">
        <span
          className={cn(
            'absolute inset-0 rounded-full bg-foreground/[0.04] transition-transform duration-700',
            status === 'speaking' && 'scale-110 animate-pulse',
          )}
        />
        <span
          className={cn(
            'absolute inset-4 rounded-full bg-foreground/[0.06] transition-transform duration-500',
            status === 'listening' && !muted && 'scale-105',
          )}
        />
        <span
          className={cn(
            'relative flex size-20 items-center justify-center rounded-full shadow-lg',
            live && !muted
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {waiting ? (
            <Loader2 className="size-7 animate-spin" />
          ) : muted ? (
            <MicOff className="size-7" />
          ) : (
            <AudioLines className="size-8" />
          )}
        </span>
      </div>

      <div className="space-y-1">
        <p role="status" className="font-display text-2xl">
          {muted && live ? 'Muted' : VOICE_STATUS_LABEL[status]}
        </p>
        {live && lines.length === 0 && status !== 'connecting' && (
          <p className="text-sm text-muted-foreground">
            Go ahead: ask about an inquiry, a consignment or a client.
          </p>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {lines.length > 0 && (
        <ol aria-label="What was said" className="w-full max-w-lg space-y-2 text-base">
          {lines.slice(-3).map((line) => (
            <li key={line.id} className={cn(line.role === 'user' && 'text-muted-foreground')}>
              <span className="font-medium">{line.role === 'user' ? 'You' : 'Beacon'}:</span>{' '}
              {line.text}
            </li>
          ))}
        </ol>
      )}

      <div className="flex items-center gap-2">
        {live && (
          <Button
            variant="outline"
            onClick={onToggleMute}
            aria-pressed={muted}
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <MicOff /> : <Mic />}
            {muted ? 'Unmute' : 'Mute'}
          </Button>
        )}
        <Button variant={live ? 'destructive' : 'outline'} onClick={onEnd}>
          <PhoneOff />
          {live ? 'End' : 'Close'}
        </Button>
      </div>
    </section>
  )
}
